import { apiFetch } from './client'
import type { User } from '../types/auth'
import type { VetSignatureRead } from '../types/stock'

export async function saveSignature(signatureData: string): Promise<VetSignatureRead> {
  const res = await apiFetch('/api/auth/me/signature', {
    method: 'PUT',
    body: JSON.stringify({ signature_data: signatureData }),
  })
  if (!res.ok) {
    const err = (await res.json()) as { detail?: string }
    throw new Error(typeof err.detail === 'string' ? err.detail : 'Failed to save signature')
  }
  return res.json() as Promise<VetSignatureRead>
}

export async function deleteSignature(): Promise<void> {
  const res = await apiFetch('/api/auth/me/signature', { method: 'DELETE' })
  if (!res.ok) throw new Error('Failed to delete signature')
}

export async function updateProfile(data: {
  full_name: string
  job_title?: string | null
  phone_number?: string | null
}): Promise<User> {
  const res = await apiFetch('/api/auth/me', {
    method: 'PUT',
    body: JSON.stringify(data),
  })
  if (!res.ok) {
    const err = (await res.json()) as { detail?: string }
    throw new Error(typeof err.detail === 'string' ? err.detail : 'Failed to update profile')
  }
  return res.json() as Promise<User>
}

export async function changePassword(data: {
  current_password: string
  new_password: string
}): Promise<void> {
  const res = await apiFetch('/api/auth/me/password', {
    method: 'PUT',
    body: JSON.stringify(data),
  })
  if (!res.ok) {
    const err = (await res.json()) as { detail?: string }
    throw new Error(typeof err.detail === 'string' ? err.detail : 'Failed to change password')
  }
}

export interface PracticeBasic {
  id: number
  name: string
}

export type ClinicalRole = 'veterinarian' | 'vet_technician' | 'receptionist' | 'non_clinical'
export type ApplicationStatus = 'invited' | 'active' | 'expired' | 'revoked' | 'pending_approval' | 'rejected'

export interface StaffApplication {
  id: number
  practice_id: number
  practice_name: string
  clinical_role: ClinicalRole
  status: ApplicationStatus
  created_at: string
}

export async function listPractices(): Promise<PracticeBasic[]> {
  const res = await apiFetch('/api/practices')
  if (!res.ok) throw new Error('Failed to load practices')
  return res.json() as Promise<PracticeBasic[]>
}

export async function applyForStaff(data: { practice_id: number; clinical_role: ClinicalRole }): Promise<StaffApplication> {
  const res = await apiFetch('/api/staff-applications', {
    method: 'POST',
    body: JSON.stringify(data),
  })
  if (!res.ok) {
    const err = (await res.json()) as { detail?: string }
    throw new Error(typeof err.detail === 'string' ? err.detail : 'Failed to submit application')
  }
  return res.json() as Promise<StaffApplication>
}

export async function myApplications(): Promise<StaffApplication[]> {
  const res = await apiFetch('/api/staff-applications/mine')
  if (!res.ok) throw new Error('Failed to load memberships')
  return res.json() as Promise<StaffApplication[]>
}

export interface StaffApplicationAdmin extends StaffApplication {
  user_id: number
  user_name: string | null
  user_email: string
}

export async function adminListApplications(): Promise<StaffApplicationAdmin[]> {
  const res = await apiFetch('/api/admin/staff-applications')
  if (!res.ok) throw new Error('Failed to load applications')
  return res.json() as Promise<StaffApplicationAdmin[]>
}

export async function adminApproveApplication(id: number): Promise<StaffApplicationAdmin> {
  const res = await apiFetch(`/api/admin/staff-applications/${id}/approve`, { method: 'PUT' })
  if (!res.ok) {
    const err = (await res.json()) as { detail?: string }
    throw new Error(typeof err.detail === 'string' ? err.detail : 'Failed to approve application')
  }
  return res.json() as Promise<StaffApplicationAdmin>
}

export async function adminRejectApplication(id: number): Promise<StaffApplicationAdmin> {
  const res = await apiFetch(`/api/admin/staff-applications/${id}/reject`, { method: 'PUT' })
  if (!res.ok) {
    const err = (await res.json()) as { detail?: string }
    throw new Error(typeof err.detail === 'string' ? err.detail : 'Failed to reject application')
  }
  return res.json() as Promise<StaffApplicationAdmin>
}
